// canvasShift.ts — Ship 6 (May 2, 2026)
//
// On-shift / off-shift session management + 5-minute GPS ping loop for the
// canvasser app. Wraps the canvas_start_shift / canvas_end_shift RPCs and
// the canvas_gps_pings table.
//
// Mental model:
//   - useCanvasShift() hook returns { shift, start(), end(), refreshing }
//   - When shift !== null (i.e. an open canvas_shifts row exists), the hook
//     spins up a 5-min interval that posts pings into canvas_gps_pings.
//     Pings pause when document.visibilityState === 'hidden' (PWA backgrounded
//     on iOS — the geolocation API throttles or stops there anyway).
//   - end() closes the row and stops the interval.
//   - On mount the hook hits canvas_active_shift() to recover from a refresh
//     mid-shift.
//
// Privacy-clean boundary: off-shift = no inserts. RLS won't even allow a
// ping insert without a matching open shift_id you own.

import { useCallback, useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { locate, recentFix, type Fix, type GeoProblem } from "./geo";

const PING_INTERVAL_MS = 5 * 60 * 1000;          // 5 minutes
const STARTUP_PING_DELAY_MS = 5_000;             // first ping 5s after toggle (so we capture an immediate location)

export interface CanvasShift {
  id: string;
  user_id: string;
  started_at: string;
  ended_at: string | null;
  start_lat: number | null;
  start_lon: number | null;
  end_lat: number | null;
  end_lon: number | null;
  ping_count: number;
  visit_count: number;
  contact_count: number;
  appointments_set: number;
  claim_count: number;
  miles_walked: number | null;
  notes: string | null;
}

interface UseCanvasShiftReturn {
  shift: CanvasShift | null;
  loading: boolean;
  starting: boolean;
  ending: boolean;
  err: string | null;
  /** Why the last location lookup failed (null once a fix comes back). */
  geoProblem: GeoProblem | null;
  /**
   * Start a new on-shift session. Captures current location into start_lat/lon.
   * requireLocation (canvassers, who are paid by the hour on shift): refuse to
   * start while location is turned off for the site, instead of opening a
   * shift with no GPS trail.
   */
  start: (opts?: { requireLocation?: boolean }) => Promise<void>;
  /** End the current shift. Captures current location into end_lat/lon. */
  end: (notes?: string) => Promise<void>;
  /** Refresh shift state from server (used after visit insert to update counters). */
  refresh: () => Promise<void>;
}

// ── Geolocation ──────────────────────────────────────────────────────────
// Lookups go through lib/geo: a fix under 2 minutes old from the canvas's
// running watch is reused, otherwise a precise lookup with a coarse fallback.
// Oct 27, 2026: this used to be a cold 8-second lookup whose errors were
// swallowed — Michael's first shifts recorded no start point and no pings.

async function pingFix(): Promise<{ fix: Fix | null; problem: GeoProblem | null }> {
  const warm = recentFix(120_000);
  if (warm) return { fix: warm, problem: null };
  const r = await locate();
  return r.ok ? { fix: r.fix, problem: null } : { fix: null, problem: r.problem };
}

// Battery API is non-standard — only Chromium supports it. iOS / Safari
// returns undefined and we just leave battery_pct null.
async function getBatteryPct(): Promise<number | null> {
  try {
    const nav: any = typeof navigator !== "undefined" ? navigator : null;
    if (!nav?.getBattery) return null;
    const battery = await nav.getBattery();
    if (typeof battery?.level !== "number") return null;
    return Math.round(battery.level * 100);
  } catch {
    return null;
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────

export function useCanvasShift(supabase: SupabaseClient): UseCanvasShiftReturn {
  const [shift, setShift] = useState<CanvasShift | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [geoProblem, setGeoProblem] = useState<GeoProblem | null>(null);

  // Use a ref so the interval callback always sees the latest shift_id
  // without re-creating the interval on every state change.
  const shiftRef = useRef<CanvasShift | null>(null);
  shiftRef.current = shift;

  const refresh = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc("canvas_active_shift");
      if (error) {
        // Non-fatal — just leave shift state as-is and surface the error
        // for the toggle so the user knows why nothing's happening.
        if (!error.message?.toLowerCase().includes("load failed")) {
          setErr(error.message);
        }
        return;
      }
      // RPC returns a row or empty string / null when no active shift.
      // Supabase returns the row directly when SETOF or ROWTYPE return.
      // canvas_active_shift returns canvas_shifts (single row or no row).
      // PostgREST gives us either an object or null.
      const row = (data && typeof data === "object" && (data as any).id) ? (data as CanvasShift) : null;
      setShift(row);
    } catch (e: any) {
      if (!String(e?.message ?? e).toLowerCase().includes("load failed")) {
        setErr(String(e?.message ?? e));
      }
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Initial probe + on-mount recovery
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Ping loop — only runs while an open shift exists.
  useEffect(() => {
    if (!shift || shift.ended_at) return;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    async function postPing() {
      if (cancelled) return;
      const current = shiftRef.current;
      if (!current || current.ended_at) return;
      // Pause when tab/PWA is backgrounded — geolocation throttles anyway and
      // the user toggled away from the canvas app.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;

      const [{ fix: pos, problem }, batt] = await Promise.all([pingFix(), getBatteryPct()]);
      if (cancelled) return;
      setGeoProblem(problem);
      if (!pos) return;

      const payload: any = {
        user_id: current.user_id,
        shift_id: current.id,
        lat: pos.lat,
        lon: pos.lon,
      };
      if (typeof pos.accuracy_m === "number") payload.accuracy_m = pos.accuracy_m;
      if (typeof pos.speed_mps === "number") payload.speed_mps = pos.speed_mps;
      if (typeof batt === "number") payload.battery_pct = batt;

      const { error } = await supabase.from("canvas_gps_pings").insert(payload);
      if (!error) {
        // Optimistic ping_count bump — server-side trigger is the source of
        // truth, but we want the UI to reflect the new count immediately.
        setShift((s) => (s && s.id === current.id ? { ...s, ping_count: s.ping_count + 1 } : s));
      }
    }

    // First ping shortly after toggle so the trail captures the start point.
    const firstTimer = setTimeout(postPing, STARTUP_PING_DELAY_MS);
    timer = setInterval(postPing, PING_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(firstTimer);
      if (timer) clearInterval(timer);
    };
  }, [shift?.id, shift?.ended_at, supabase]);

  // Auto-refresh shift counters when the tab regains focus — handles the
  // case where canvas_visits triggers updated visit_count while the user
  // was away.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => {
      if (document.visibilityState === "visible" && shiftRef.current && !shiftRef.current.ended_at) {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refresh]);

  // Subscribe to UPDATE events on canvas_shifts so visit counters refresh
  // in real time as visits land.
  useEffect(() => {
    if (!shift || shift.ended_at) return;
    const channel = supabase
      .channel(`canvas_shift:${shift.id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "canvas_shifts", filter: `id=eq.${shift.id}` },
        (payload: any) => {
          if (payload?.new) {
            setShift((s) => (s && s.id === payload.new.id ? { ...s, ...payload.new } : s));
          }
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [shift?.id, shift?.ended_at, supabase]);

  const start = useCallback(async (opts?: { requireLocation?: boolean }) => {
    setStarting(true);
    setErr(null);
    try {
      const r = await locate({ maxAgeMs: 120_000 });
      const pos = r.ok ? r.fix : null;
      setGeoProblem(r.ok ? null : r.problem);
      if (!r.ok && r.problem === "denied" && opts?.requireLocation) {
        setErr("Turn on location to go on shift. Your hours are tracked by GPS.");
        return;
      }
      const { data, error } = await supabase.rpc("canvas_start_shift", {
        p_lat: pos?.lat ?? null,
        p_lon: pos?.lon ?? null,
      });
      if (error) {
        setErr(error.message);
        return;
      }
      const row = (data && typeof data === "object" && (data as any).id) ? (data as CanvasShift) : null;
      setShift(row);
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally {
      setStarting(false);
    }
  }, [supabase]);

  const end = useCallback(async (notes?: string) => {
    setEnding(true);
    setErr(null);
    try {
      // Ending shouldn't hang on a weak signal: a recent fix, or one quick try.
      const r = await locate({ maxAgeMs: 120_000, timeoutMs: 6_000, coarseFallback: false });
      const pos = r.ok ? r.fix : null;
      const { data, error } = await supabase.rpc("canvas_end_shift", {
        p_lat: pos?.lat ?? null,
        p_lon: pos?.lon ?? null,
        p_miles: null,
        p_notes: notes ?? null,
      });
      if (error) {
        setErr(error.message);
        return;
      }
      // After end, immediately drop shift state — UI flips back to "Off shift"
      const _row = (data && typeof data === "object" && (data as any).id) ? (data as CanvasShift) : null;
      void _row;
      setShift(null);
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally {
      setEnding(false);
    }
  }, [supabase]);

  return { shift, loading, starting, ending, err, geoProblem, start, end, refresh };
}
